<template>
  <div class="map-elem-info land-ai-detections">
    <div class="col-icon">
      <img class="resource-icon" :src="iconPath">
    </div>
    <div class="col-info">
      <div class="row1 mb6">
        <div class="left-side">
          <div class="ft-bold mb6">
            <span class="ib lbl">日時: </span>
            <span class="ib val">{{ dateDisp }}</span>
            <button type="button" key="msearch" class="btn btn-sm btn-secondary ml12" @click="doAreaMovieSearch">
              <i class="fas fa-search mr2"></i>周辺の動画を検索
            </button>
          </div>
          <div class="kp-info-disp mb6 ">
            <span class="ib lbl">
              場所: {{kpDisp}}
            </span>
            <span class="angle-disp ml16" v-show="hasValidAngle">
              <span class="ib mr2">方角: {{state.detection.angle}}°</span>
              <div class="angle-arrow-wrap">
                <div class="angle-arrow-bg">
                </div>
                <div class="angle-arrow-img" :style="{ transform: `rotateZ(${state.detection.angle}deg)`}">
                </div>
              </div>
            </span>
          </div>
          <div class="mb6">
            <span class="ib lbl">種別: </span>
            <span class="ib val">{{state.detection.detectionKindDisp}}</span>
            <template v-if="isHosouDamage">
              <span class="ib lbl ml12">閾値: </span>
              <span class="ib val">{{state.detection.contourAreaMax}}</span>
            </template>
            <template v-if="isJointDamage">
              <span class="ib lbl ml12">ジョイント種別: </span>
              <span class="ib val">{{state.detection.jointTypesDisp}}</span>
            </template>
          </div>
          <div class="mb6" v-if="state.detection.comment">
            <span class="ib val">{{ state.detection.commentDisp }}</span>
          </div>
          <div class="image-preview-area">
            <div class="image-box" v-if="savedImage">
              <img :src="savedImage" class="image" @click="showImage"/>
            </div>
          </div>
        </div>
        <div class="right-side mr8">
          <div class="move-buttons mt2">
            <button class="btn btn-primary btn-xs mr4" @click="showPrevious">
              <i class="fas fa-arrow-left"></i>
              前へ
            </button>
            <button class="btn btn-primary btn-xs" @click="showNext">
              次へ
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="row2">
        <div class="left-side">
        </div>
        <div class="buttons ta-right mr8" v-if="!!state.detection.detectionsLink">
          <button class="btn btn-sm btn-info" @click="openDetection">
            AI検知結果を開く
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import extLandAiDetectionsApi from 'src/apis/extLandAiDetections';
import {
  onMounted,
  watch,
  reactive,
  computed,
} from 'vue';
import type { MapElemInfo } from 'src/models';
import { validateAngle } from 'src/lib/commentHelper';
import useMapElemInfoImagePreview from 'src/composables/useMapElemInfoImagePreview';
import { dtFormat } from 'src/lib/dateHelper';
import type { GIDetection } from 'src/models/geoItem';

interface MapElemInfoLandAiDetectionsState {
  detection: GIDetection;
}

interface Props {
  elem: MapElemInfo<GIDetection>;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: string, ...args: unknown[]): void;
}>();

const state = reactive<MapElemInfoLandAiDetectionsState>({
  detection: props.elem.data,
});
const dateDisp = computed<string>(() => dtFormat(new Date(state.detection.imgTs)));
const modalTitle = computed<string>(() => `${dateDisp.value} - ${kpDisp.value}`);
const downloadFilenameTemplate = computed<string>(() => {
  const detectionsTs = dtFormat(new Date(state.detection.imgTs), 'yyyymmddHHMMSS');
  return `${state.detection.detectionKindDisp}_${detectionsTs}_$TIMESTAMP.jpg`;
});
const skipFetchingImage = computed<boolean>(() => {
  return !state.detection?.imgPath;
});
const { showPrevious, showNext, savedImage, showImage } = useMapElemInfoImagePreview({
  emit,
  elem: computed(() => props.elem),
  refMapElemInfoData: computed(() => state.detection),
  skipFetchingImage,
  fetchApiFn: () => {
    return extLandAiDetectionsApi.getImage({
      img_path: state.detection.imgPath,
    });
  },
  modalTitle,
  downloadFilenameTemplate,
});

const initDisplay = () => {
  state.detection = props.elem.data;
};
onMounted(initDisplay);
watch(() => props.elem, initDisplay);

const getLocationDispOfKp = (): string => {
  // roadNameDispが空だったら「場所なし」と表示する
  const roadNameDisp = state.detection.roadNameDisp ? state.detection.roadNameDisp : '場所なし';
  // 全角のカッコが割と場所取るので半角に置換
  const placeName = !state.detection.placeName || state.detection.placeName === 'main_line'
    ? ''
    : state.detection.placeName
      .replace(/（/g, '(')
      .replace(/）/g, ')');
  // 小数第二位まで考慮
  const kpDisp = state.detection.kp
    ? `${parseFloat(state.detection.kp).toFixed(2).replace(/0$/, '')}km`
    : '';
  return [
    roadNameDisp,
    state.detection.direction,
    placeName,
    kpDisp,
  ].filter(e => !!e).join(' ');
};
const kpDisp = computed<string>(getLocationDispOfKp);
const iconPath = `/static/img/land_ai_detection_${state.detection.detectionKind}.png`;
const hasValidAngle = computed(() => {
  return validateAngle(state.detection.angle);
});
const detectionsLink = computed<string>(() => {
  if (state.detection.roadNameDisp) {
    return state.detection.detectionsLink;
  }
  // 場所なしの場合、遷移先に場所なしも表示するため、without_landmarkをつける
  return `${state.detection.detectionsLink}&without_landmark`;
});
const isJointDamage = computed(() => {
  return state.detection.detectionKind === 'joint_damage';
});
const isHosouDamage = computed(() => {
  return !isJointDamage.value;
});

const openDetection = () => {
  window.open(detectionsLink.value, '_blank');
};
const doAreaMovieSearch = () => {
  emit('area-movie-search', {
    lat: props.elem.data.location.lat,
    lon: props.elem.data.location.lon,
    radius: 500,
  });
};
</script>

<style lang="scss" scoped>
.map-elem-info {
  .col-icon {
    margin-right: 4px;
    display: flex;
    flex-direction: column;
    .resource-icon {
      width: 42px;
      &.start {
        margin: -4px 0 0 0;
      }
      &.end {
        margin: -30px 0 0 0;
      }
    }
  }
  .col-info {
    .row1, .row2 {
      display: flex;
      justify-content: space-between;
    }
    .row1 {
      flex-wrap: wrap;
    }
    .lbl {
      margin-right: 4px;
    }
    .left-side {
      flex-grow: 1;
    }
    .buttons {
      width: 152px;
      min-width: 152px;
      align-self: flex-end;
    }
    .image-preview-area {
      display: flex;
      width: 100%;
      .image-box {
        cursor: pointer;
        position: relative;
        border: 1px solid #dddddd;
        margin-left: 6px;
        .image {
          max-height: 72px;
          max-width: 128px;
        }
      }
    }
    .kp-info-disp {
      display: flex;
    }
    .angle-disp {
      display: flex;
    }
    .angle-arrow-wrap {
      display: inline-block;
      width: 22px;
      height: 22px;
      margin-top: -1px;
      position: relative;
      .angle-arrow-bg {
        position: absolute;
        width: 22px;
        height: 22px;
        top: 0;
        left: 0;
        background-image: url('/static/img/comment-angle-bg.png');
        background-size: cover;
      }
      .angle-arrow-img {
        position: absolute;
        width: 22px;
        height: 22px;
        top: 0;
        left: 0;
        background-image: url('/static/img/comment-angle.png');
        background-size: cover;
      }
    }
  }
}
</style>
